import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { NavLink, useParams } from "react-router-dom";
import { Button, Spinner, Switch, Textarea, Input, Chip, useDisclosure } from "@heroui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useStyles } from "@/hooks/useStyles";
import DataTable from "@/components/common/table/DataTable";
import AddGroupMemberModal from "../common/modal/AddGroupMemberModal";

import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { MdOutlineAutoMode } from "react-icons/md";
import { CgUnavailable } from "react-icons/cg";
import { IoIosArrowBack, IoMdAdd } from "react-icons/io";
import { IoSearchSharp } from "react-icons/io5";
import { FaRegTrashCan } from "react-icons/fa6";

type CustomerSettingsType = {
  status_automation: string;
  customer_instruction: string;
  customer_group_id: string;
  customer_group_alias: string;
};

type MemberType = {
  customer_id: string;
  customer_name: string;
  source: string;
  status: string;
  creation_time: string;
  update_time: string;
};

interface Params {
  [key: string]: string | undefined;
  userId: string;
}

type FormValues = {
  status_automation: string;
  customer_instruction: string;
  customer_group_id: string;
  customer_group_alias: string;
};

export default function CustomerGroupSettingsEdit() {
  const { t } = useTranslation();
  const styles = useStyles();
  const params = useParams<Params>();
  const axiosPrivate = useAxiosPrivate();

  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [submitting, setSubmitting] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [dataCustomerSettings, setDataCustomerSettings] =
    useState<CustomerSettingsType | null>(null);
  const methods = useForm<FormValues>({
    mode: "onBlur",
  });

  const { data: membersResponse, isLoading: membersLoading } = useQuery<{
    data: MemberType[];
    pagination: any;
  }>({
    queryKey: ["CustomerGroupMembers", params.customerGroupId],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/customers_group/settings/${params.customerGroupId}/members?per_page=1000`,
      );
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const membersData = React.useMemo(
    () => membersResponse?.data ?? [],
    [membersResponse],
  );

  const filteredMembers = React.useMemo(() => {
    let filtered = [...membersData];
    if (filterValue) {
      const search = filterValue.toLowerCase();
      filtered = filtered.filter(
        (member) =>
          member.customer_name.toLowerCase().includes(search) ||
          member.customer_id.toLowerCase().includes(search),
      );
    }
    filtered.sort((a, b) => {
      if (a.source === b.source) return 0;
      return a.source === "manual" ? -1 : 1;
    });
    return filtered;
  }, [filterValue, membersData]);

  const membersTopContent = (
    <Input
      isClearable
      classNames={{
        base: "w-full min-w-[75%]",
        inputWrapper: [
          "border-0",
          "bg-white",
          "h-9",
          "min-h-unit-9",
          "hover:bg-white",
          "group-data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white",
        ],
        input: "text-vistagrey",
      }}
      placeholder={t("search")}
      size="md"
      startContent={<IoSearchSharp className="text-default-300" />}
      value={filterValue}
      onClear={() => setFilterValue("")}
      onValueChange={setFilterValue}
    />
  );

  const handleDeleteMember = useCallback(
    async (customerId: string) => {
      try {
        await axiosPrivate.delete(
          `/customers_group/settings/${params.customerGroupId}/members/${customerId}`,
        );
        queryClient.invalidateQueries({
          queryKey: ["CustomerGroupMembers", params.customerGroupId],
        });
      } catch (error) {
        console.error("Error deleting member from group:", error);
      }
    },
    [axiosPrivate, params.customerGroupId, queryClient],
  );

  const membersColumns = [
    { name: t("customer_id"), uid: "customer_id", sortable: true },
    { name: t("customer_name"), uid: "customer_name", sortable: true },
    { name: t("source"), uid: "source", sortable: true },
    { name: t("actions"), uid: "actions" },
  ];

  const renderMemberCell = useCallback(
    (member: MemberType, columnKey: React.Key) => {
      switch (columnKey) {
        case "customer_id":
          return <span className="text-xs">{member.customer_id}</span>;
        case "customer_name":
          return <span>{member.customer_name}</span>;
        case "source":
          return (
            <Chip
              size="sm"
              variant="flat"
              color={member.source === "import" ? "primary" : "warning"}
            >
              {member.source}
            </Chip>
          );
        case "actions":
          return (
            <button
              className="flex items-center justify-center w-full"
              onClick={() => handleDeleteMember(member.customer_id)}
            >
              <FaRegTrashCan />
            </button>
          );
        default:
          return null;
      }
    },
    [handleDeleteMember],
  );

  const tableClassNames = React.useMemo(
    () => ({
      table: ["border-separate", "border-spacing-y-2"],
      th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
      td: [
        "bg-white",
        "p-3",
        "group-data-[first=true]:first:before:rounded-none",
        "group-data-[first=true]:last:before:rounded-none",
        "group-data-[middle=true]:before:rounded-none",
        "group-data-[last=true]:first:before:rounded-none",
        "group-data-[last=true]:last:before:rounded-none",
      ],
    }),
    [],
  );

  useEffect(() => {
    const fetchCustomerSettings = async () => {
      try {
        const response = await axiosPrivate.get(
          `/customers_group/settings/${params.customerGroupId}`,
        );
        const customerSettingsData = response.data;
        setDataCustomerSettings(customerSettingsData);

        methods.reset({
          ...customerSettingsData,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données client :",
          error,
        );
      }
    };
    fetchCustomerSettings();
  }, [params.customerGroupId, axiosPrivate, methods]);

  const onSubmit = async (formData: any) => {
    setSubmitting(true);
    const formattedData = {
      status_automation: formData.status_automation,
      customer_instruction: formData.customer_instruction,
      customer_group_alias: formData.customer_group_alias
    };
    await axiosPrivate.put(
      `/customers_group/settings/${params.customerGroupId}`,
      formattedData,
    );
    setSubmitting(false);
  };

  return (
    <>
      <div className="w-full text-vistagrey my-6 inline-flex">
        <div>
          <div className="text-vistagrey text-xl">
            <AiOutlineUsergroupAdd className="fill-vistagreen inline-block text-4xl mr-2" />
            {t("customer_group_settings")}
          </div>
          <div className="ml-10">
            <NavLink
              to={`/customers/`}
            >
              <Button
                color="primary"
                variant="bordered"
                size="sm"
                radius="full"
                startContent={<IoIosArrowBack />}
              >
                {t("back")}
              </Button>
            </NavLink>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg w-full">
        {dataCustomerSettings && (
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col items-start gap-4"
            >
              <div className="relative w-full">
                <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold">
                  {t("edit_customer_group_settings")}
                </div>
                <div className="space-y-6 p-5">
                  <Controller
                    name="customer_group_id"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_group_id}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        label={t("customer_group_id")}
                        placeholder={t("customer_group_id")}
                        variant="flat"
                        labelPlacement="outside"
                        readOnly
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="customer_group_alias"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_group_alias}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        label={t("customer_group_alias")}
                        placeholder={t("customer_group_alias")}
                        variant="flat"
                        labelPlacement="outside"
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="status_automation"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.status_automation}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size="md"
                        color="primary"
                        startContent={<MdOutlineAutoMode />}
                        endContent={<CgUnavailable />}
                        isSelected={value === "auto"}
                        onValueChange={(isSelected) =>
                          onChange(isSelected ? "auto" : "pending_review")
                        }
                        classNames={styles}
                      >
                        {t("mode_auto")}
                      </Switch>
                    )}
                  />

                  <Controller
                    name="customer_instruction"
                    control={methods.control}
                    defaultValue={dataCustomerSettings.customer_instruction}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        label={t("instructions")}
                        placeholder={t("enter_your_instructions")}
                        classNames={styles}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />

                  <Button
                    type="submit"
                    className="bg-vistagreen text-white rounded-md p-3"
                  >
                    {submitting && <Spinner color="default" />}
                    {!submitting && <div>{t("validate_changes")}</div>}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </div>

      <div className="bg-white rounded-lg w-full mt-6">
        <div className="border-b-1 border-dashed border-vistagrey/30 p-3 font-bold flex justify-between items-center">
          {t("group_members")}
          <Button
            size="sm"
            radius="full"
            className="bg-vistagreen text-white"
            startContent={<IoMdAdd />}
            onPress={onOpen}
          >
            {t("add")}
          </Button>
        </div>
        <div className="p-5">
          {membersLoading ? (
            <div className="flex justify-center py-8">
              <Spinner color="primary" />
            </div>
          ) : (
            <DataTable<MemberType>
              columns={membersColumns}
              data={filteredMembers}
              renderCell={renderMemberCell}
              keyExtractor={(member) => member.customer_id}
              initialSortDescriptor={{
                column: "source",
                direction: "descending",
              }}
              topContent={membersTopContent}
              classNames={tableClassNames}
              selectionMode="none"
              itemsPerPageOptions={[50, 100]}
              hideColumnSelector
            />
          )}
        </div>
      </div>

      <AddGroupMemberModal
        isOpen={isOpen}
        onClose={onClose}
        customerGroupId={params.customerGroupId ?? ""}
      />
    </>
  );
}
